import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useLogin } from '@/api/queries/auth-queries'
import { getOrganization } from '@/api/services/organization'
import dsigLogo from '@/assets/dsig_logo.webp'
import kaeferLogo from '@/assets/KAEFER_Logo_www_pos.svg'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ROUTES } from '@/constants/routes'
import { useLoginForm } from '@/hooks/forms/use-login-form'
import type { LoginFormData } from '@/schemas/login-schema'
import { useAuthStore } from '@/store'
import { useProfileStore } from '@/store/profile-store'
import { getApiErrorMessage } from '@/utils/api-error'

const appVersion = import.meta.env.VITE_VERSION || 'dev'

// Só aceita caminhos internos, para não virar open redirect
function getRedirectTarget(state: unknown): string {
	const from = (state as { from?: unknown } | null)?.from
	if (
		typeof from === 'string' &&
		from.startsWith('/') &&
		!from.startsWith('//')
	) {
		return from
	}
	return ROUTES.DASHBOARD.HOME
}

export const LoginPage = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const redirectTo = getRedirectTarget(location.state)
	const loginStore = useAuthStore((s) => s.login)
	const setDefaultBranch = useProfileStore((s) => s.setDefaultBranch)
	const setDefaultContract = useProfileStore((s) => s.setDefaultContract)
	const setDefaultCustomer = useProfileStore((s) => s.setDefaultCustomer)
	const { mutateAsync, isPending } = useLogin()

	const [showPassword, setShowPassword] = useState(false)
	const [isError, setIsError] = useState(false)
	const [error, setError] = useState('')

	const form = useLoginForm()
	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = form

	const onSubmit = async (formData: LoginFormData) => {
		setIsError(false)

		try {
			const data = await mutateAsync({
				registration: formData.registration,
				password: formData.password,
			})

			loginStore(data.user)

			if (data.user.crId) {
				try {
					const org = await getOrganization()
					const branch = org.find((b) =>
						b.crs.some((cr) => cr.id === data.user.crId),
					)
					if (branch) {
						setDefaultBranch(branch.id, branch.name)
						setDefaultContract(data.user.crId, data.user.crName!)
						setDefaultCustomer('', '') // reset default customer, as we don't have that info at login time
					}
				} catch {
					// non-fatal – user can select defaults manually
				}
			}

			if (data.user.shouldChangePassword) {
				navigate(ROUTES.PUBLIC.FORCE_CHANGE_PASSWORD, { replace: true })
				return
			}

			toast.success('Login realizado com sucesso!')
			navigate(redirectTo, { replace: true })
		} catch (error) {
			setIsError(true)
			setError(getApiErrorMessage(error))
		}
	}

	// ─── Shared form ──────────────────────────────────────────────
	const formContent = (
		<Form {...form}>
			<form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
				<FormField
					name="registration"
					control={control}
					render={({ field }) => (
						<FormItem label="Matrícula" error={errors.registration?.message}>
							<div className="flex h-11 items-center rounded-md border border-kaefer-silver px-3 focus-within:border-kaefer-dark-grey">
								<Input
									name={field.name}
									ref={field.ref}
									className="flex-1 border-0 p-0 shadow-none outline-none focus-visible:ring-0"
									disabled={isPending}
									id="registration"
									placeholder="Digite sua matrícula"
									type="text"
									value={field.value}
									onChange={field.onChange}
								/>
							</div>
						</FormItem>
					)}
				/>

				<FormField
					name="password"
					control={control}
					render={({ field }) => (
						<FormItem label="Senha" error={errors.password?.message}>
							<div className="flex justify-end">
								<Link
									className="text-kaefer-medium-grey text-xs transition-colors hover:text-kaefer-dark-grey"
									to={ROUTES.PUBLIC.RECOVER_PASSWORD}
								>
									Esqueci minha senha
								</Link>
							</div>
							<div className="flex h-11 items-center rounded-md border border-kaefer-silver px-3 focus-within:border-kaefer-dark-grey">
								<Input
									name={field.name}
									ref={field.ref}
									className="flex-1 border-0 p-0 shadow-none outline-none focus-visible:ring-0"
									disabled={isPending}
									id="password"
									placeholder="••••••••"
									type={showPassword ? 'text' : 'password'}
									value={field.value}
									onChange={field.onChange}
								/>
								<Button
									variant="password-icon"
									size="icon-sm"
									disabled={isPending || isSubmitting}
									onClick={() => setShowPassword(!showPassword)}
									type="button"
								>
									{showPassword ? (
										<EyeOff className="h-4 w-4" />
									) : (
										<Eye className="h-4 w-4" />
									)}
								</Button>
							</div>
						</FormItem>
					)}
				/>

				{/* Error */}
				{isError && (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				{/* Submit */}
				<Button
					className="h-11 w-full font-medium text-base"
					disabled={isPending || isSubmitting}
					type="submit"
					variant="kaefer-grey"
				>
					{isPending ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Entrando...
						</>
					) : (
						'Entrar'
					)}
				</Button>
			</form>
		</Form>
	)

	return (
		<div className="min-h-dvh bg-white sm:flex sm:items-center sm:justify-center sm:bg-kaefer-light-silver sm:p-6">
			<div className="flex min-h-dvh w-full flex-col sm:min-h-0 sm:max-w-md sm:rounded-2xl sm:bg-white sm:shadow-xl">
				{/* Header — logo do sistema */}
				<div className="flex flex-col items-center bg-kaefer-dark-grey px-6 pb-6 pt-8 text-center sm:rounded-t-2xl sm:px-8">
					<span className="relative inline-block">
						<img
							alt="D-SIG"
							className="block h-44 w-auto drop-shadow-xl"
							src={dsigLogo}
						/>
						{/* Pálpebra posicionada sobre o olho da águia */}
						<span className="pointer-events-none absolute top-[24.3%] left-[48.1%] h-[6.2%] w-[5%] -translate-x-1/2 -translate-y-1/2 scale-y-0 rounded-full border-[#140e08] border-b-2 bg-[#34281d] motion-safe:animate-[eagle-blink-idle_5s_ease-in-out_1.5s_infinite]" />
					</span>
				</div>

				{/* Form area */}
				<div className="flex-1 px-6 py-8 sm:px-8 sm:pb-6 sm:pt-6">
					{formContent}

					{/* Logo corporativo */}
					<div className="mt-8 flex items-center gap-4">
						<div className="h-px flex-1" />
						<img
							alt="KRIP Serviços Industriais"
							className="h-10 w-auto"
							src={kaeferLogo}
						/>
						<div className="h-px flex-1" />
					</div>

					<p className="mt-4 text-center text-kaefer-silver text-xs">
						SSMA {appVersion}
					</p>
				</div>
			</div>
		</div>
	)
}
